import { useEffect, useRef } from 'react'
import { Feature } from 'ol'
import { Point, Circle as CircleGeom } from 'ol/geom'
import { Style, Icon, Fill, Stroke } from 'ol/style'
import { fromLonLat } from 'ol/proj'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Map } from 'ol'

interface UserLocationMarkerProps {
  map: Map | null
  location: {
    latitude: number
    longitude: number
    accuracy: number
  } | null
  heading: number | null   // ° od N, zgodnie z ruchem wskazówek; null = brak kierunku
  isVisible: boolean
}

export default function UserLocationMarker({
  map,
  location,
  heading,
  isVisible
}: UserLocationMarkerProps) {
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const featureRef = useRef<Feature | null>(null)
  const accuracyFeatureRef = useRef<Feature | null>(null)

  // Marker "jesteś tutaj": niebieska kropka z białą obwódką; gdy znany kierunek,
  // dodatkowo stożek kierunkowy (jak w Mapach Google).
  const createUserIcon = (hdg: number | null) => {
    const cone = hdg != null
      ? `<path d="M24 24 L14 6 L34 6 Z" fill="#4285F4" fill-opacity="0.35" transform="rotate(${hdg} 24 24)"/>`
      : ''
    const svg = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        ${cone}
        <circle cx="24" cy="24" r="9" fill="#FFFFFF"/>
        <circle cx="24" cy="24" r="6.5" fill="#4285F4"/>
      </svg>
    `
    return `data:image/svg+xml;base64,${btoa(svg)}`
  }

  // Styl okręgu dokładności (półprzezroczysty niebieski)
  const accuracyStyle = new Style({
    fill: new Fill({ color: 'rgba(66, 133, 244, 0.12)' }),
    stroke: new Stroke({ color: 'rgba(66, 133, 244, 0.5)', width: 1 })
  })

  // Update marker position and rotation
  useEffect(() => {
    if (!map || !isVisible) {
      // Remove layer if exists
      if (layerRef.current) {
        if (map) {
          map.removeLayer(layerRef.current)
        }
        layerRef.current = null
        featureRef.current = null
        accuracyFeatureRef.current = null
      }
      return
    }

    // Create or update feature
    if (location) {
      const coordinates = fromLonLat([location.longitude, location.latitude])

      // Promień okręgu dokładności w jednostkach EPSG:3857.
      // Web Mercator rozciąga odległości o 1/cos(szerokości), więc korygujemy,
      // aby okrąg odpowiadał realnej dokładności w metrach.
      const accuracyRadius = (location.accuracy && location.accuracy > 0)
        ? location.accuracy / Math.cos(location.latitude * Math.PI / 180)
        : 0

      // --- Okrąg dokładności ---
      if (accuracyRadius > 0) {
        if (!accuracyFeatureRef.current) {
          accuracyFeatureRef.current = new Feature({ geometry: new CircleGeom(coordinates, accuracyRadius) })
          accuracyFeatureRef.current.setStyle(accuracyStyle)
        } else {
          const geom = accuracyFeatureRef.current.getGeometry()
          if (geom instanceof CircleGeom) {
            geom.setCenter(coordinates)
            geom.setRadius(accuracyRadius)
          }
        }
      }

      // --- Marker pozycji ---
      if (!featureRef.current) {
        featureRef.current = new Feature({
          geometry: new Point(coordinates),
          userLocation: true
        })
      } else {
        const geometry = featureRef.current.getGeometry()
        if (geometry instanceof Point) {
          geometry.setCoordinates(coordinates)
        }
      }

      const iconStyle = new Style({
        image: new Icon({
          src: createUserIcon(heading),
          scale: 1,
          anchor: [0.5, 0.5],
        })
      })
      featureRef.current.setStyle(iconStyle)

      // Create or update layer
      if (!layerRef.current) {
        const vectorSource = new VectorSource()
        if (accuracyFeatureRef.current) vectorSource.addFeature(accuracyFeatureRef.current)
        vectorSource.addFeature(featureRef.current)

        layerRef.current = new VectorLayer({ source: vectorSource })
        ;(layerRef.current as any).set('id', 'user-location')
        map.addLayer(layerRef.current)
      } else {
        const source = layerRef.current.getSource()
        if (source) {
          if (accuracyFeatureRef.current && !source.getFeatures().includes(accuracyFeatureRef.current)) {
            source.addFeature(accuracyFeatureRef.current)
          }
          if (!source.getFeatures().includes(featureRef.current)) {
            source.addFeature(featureRef.current)
          }
        }
      }
    }
  }, [map, location, heading, isVisible])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (layerRef.current && map) {
        map.removeLayer(layerRef.current)
      }
    }
  }, [map])

  return null // This component doesn't render anything directly
}
